import { useBackgroundImage } from '@/hooks/useBackgroundImage';
import { BackgroundImageUploader } from '@/components/BackgroundImageUploader';

interface HeroAction {
  icon: React.ComponentType<{ className?: string }>;
  label?: string;
  onClick: () => void;
}

interface ModernHeroProps {
  title: string;
  subtitle?: string;
  actions: HeroAction[];
  backgroundImage: string;
  category: string; // 'calendar', 'tasks', 'persons', 'dashboard', etc.
  familyId?: string; // Optional family ID for clan-specific backgrounds
  allowImageChange?: boolean;
  className?: string;
  height?: string;
}

export function ModernHero({
  title,
  subtitle,
  actions,
  backgroundImage,
  category,
  familyId,
  allowImageChange = true,
  className = '',
  height = '60vh'
}: ModernHeroProps) {
  
  const { 
    currentImage, 
    updateBackgroundImage, 
    resetToDefault, 
    isLoading 
  } = useBackgroundImage({
    defaultImage: backgroundImage,
    category,
    familyId,
    fallbackToDefault: true
  });

  return (
    <div className={className}>
      {/* Hero Section */}
      <div className="relative overflow-hidden" style={{ height }}>
        {/* Background avec uploader intégré */}
        <BackgroundImageUploader
          currentImage={currentImage}
          onImageUpload={updateBackgroundImage}
          onResetToDefault={resetToDefault}
          isLoading={isLoading}
          allowImageChange={allowImageChange}
          className="absolute inset-0"
        />

        {/* Hero Content */}
        <div 
          className="absolute inset-0 flex flex-col justify-center items-center text-white px-6 z-10 pointer-events-none" 
          style={{ zIndex: 10 }}
        >
          <h1 className="text-4xl font-bold mb-2 text-center drop-shadow-lg">
            {title}
          </h1>
          
          {subtitle && (
            <p className="text-lg text-center mb-8 drop-shadow-lg opacity-90 max-w-md">
              {subtitle}
            </p>
          )}
          
          {/* Action Buttons */}
          <div className={`pointer-events-auto flex gap-4 mb-8 justify-center ${actions.length === 4 ? 'grid grid-cols-2' : 'flex-wrap max-w-xs mx-auto'}`}>
            {actions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                  className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-200 shadow-lg border border-white/20 hover:scale-105"
                  title={action.label}
                >
                  <Icon className="h-6 w-6 text-white" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}